/*
==========================================
STAMP CANVAS TEXT RENDERER  v1.0.0
文件: assets/stamp-canvas-text-renderer.js

!! 不要在这个文件里写产品特定代码 !!
!! 只负责在 Canvas 上正确渲染多行文字  !!

【为什么需要这个文件】
  产品8（image-left）使用 HTML/CSS DOM 渲染文字。
  这种方式有一个已知 BUG：CSS letter-spacing 会在每个字符之间
  强制插入间距，破坏 Great Vibes / Sacramento / Dancing Script
  等花体连笔字的连笔效果（字符之间出现断开的空隙）。

  产品9（address-label）使用 Canvas 2D API 渲染文字，
  花体字的连笔效果完全正常，因为 Canvas fillText() 不强制
  加入 CSS letter-spacing。

  本文件将产品9的 Canvas 文字渲染逻辑提炼为独立的无状态工具，
  任何产品只需一次函数调用即可获得和产品9完全一致的文字效果。

【核心特性】
  ✓ 纯渲染器 — 无状态，不持有字体/颜色，不操作 DOM
  ✓ 花体字自动保护 — 检测到花体字时强制 letterSpacing=0，确保连笔
  ✓ 自动缩小字号 — measureFit() 确保文字不超出可用宽度
  ✓ 多行独立字体 — 每行可独立设定字体/字重/字号/字间距/颜色
  ✓ 对齐方式可配 — 支持 'left' | 'center' | 'right'
  ✓ 行高可配置 — 每行独立 lineHeightFactor（默认1.2）
  ✓ 分组间隙 — 第0行（Name）与后续行之间可加额外 gap
  ✓ 占位文字支持 — text 为空时自动渲染灰色占位文字
  ✓ 返回渲染结果 — 返回每行实际渲染的字号，供宿主调试/二次布局使用

【API】

  window.StampCanvasTextRenderer.drawLines(ctx, options) → result

  options 字段：
    lines          Array   必须  每行配置（详见下方 Line 格式）
    x              Number  必须  文字区左边界（Canvas 逻辑像素）
    y              Number        文字区顶部偏移（默认0，垂直居中在 canvasHeight 内）
    maxWidth       Number  必须  文字区最大宽度
    canvasHeight   Number  必须  Canvas 总高度（用于垂直居中计算）
    align          String        'left' | 'center' | 'right'（默认 'left'）
    groupGap       Number        第0行与第1行之间的额外间隙（默认4px）
    offsetY        Number        整体垂直偏移（拖拽偏移量，默认0）
    placeholderText String       所有行均为空时显示的占位文字
    placeholderFont String       占位文字字体（默认第一行字体）
    placeholderSize Number       占位文字字号（默认第一行字号 * 0.82）
    placeholderColor String      占位文字颜色（默认 '#d0c8c0'）

  Line 格式（lines 数组中每个元素）：
    text           String  必须  要渲染的文字（空字符串 = 跳过该行）
    font           String  必须  字体名称，例如 'Great Vibes'
    size           Number  必须  初始字号（会自动缩小以适应 maxWidth）
    weight         Number        字重（默认700）
    color          String        墨水颜色（默认继承 ctx.fillStyle）
    letterSpacing  Number        额外字符间距px（花体字自动强制为0）
    lineHeightFactor Number      行高系数（默认1.2，即 size * 1.2 = 行高）
    minSize        Number        最小字号下限（默认10）

  返回值 result：
    { renderedLines: [{ text, font, size, lineH }] }
    renderedLines — 实际渲染的每行参数（size 是缩小后的实际字号）

  工具函数：
    window.StampCanvasTextRenderer.isScriptFont(fontName) → Boolean
      判断一个字体是否是花体连笔字（letter-spacing 应为0）

  使用示例（产品8 image-left，在 canvas 文字区渲染）：
    const result = window.StampCanvasTextRenderer.drawLines(ctx, {
      lines: [
        { text: nameLine,  font: 'Great Vibes',  size: 38, weight: 400, color: '#1a1a1a' },
        { text: addrLine1, font: 'Montserrat',   size: 24, weight: 700, color: '#1a1a1a' },
        { text: addrLine2, font: 'Montserrat',   size: 24, weight: 700, color: '#1a1a1a' },
        { text: addrLine3, font: 'Montserrat',   size: 24, weight: 700, color: '#1a1a1a' },
      ],
      x: 120, maxWidth: 280, canvasHeight: 480,
      align: 'left', groupGap: 4,
      offsetY: textOffsetY
    });

==========================================
*/

window.StampCanvasTextRenderer = (function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────
     花体连笔字名单
     这些字体的连笔效果依赖字符的出入笔在视觉上无缝衔接，
     一旦加入 letter-spacing 字符就会断开。
     检测到以下字体时，letterSpacing 强制覆盖为 0。
  ───────────────────────────────────────────────────────── */
  const SCRIPT_FONT_PATTERN = /great.?vibes|sacramento|dancing.?script|lovers.?quarrel|clicker.?script|allura|alex.?brush|parisienne|satisfy|marck.?script|yellowtail|cookie|pacifico|pinyon.?script|rouge.?script|pinstripe|italianno|playlist|milkshake|arizonia|oooh.baby|cinzel.?decorative/i;

  /**
   * 判断字体名是否是花体连笔字（letter-spacing 应为0）
   * @param  {string} fontName
   * @returns {boolean}
   */
  function isScriptFont(fontName) {
    return SCRIPT_FONT_PATTERN.test(fontName || '');
  }

  /* ─────────────────────────────────────────────────────────
     格式化 ctx.font 字符串
  ───────────────────────────────────────────────────────── */
  function _buildFont(weight, sizeP, fontName) {
    // 字体名含空格时加引号，确保浏览器正确解析
    const family = /\s/.test(fontName) ? `'${fontName}'` : fontName;
    return `${weight} ${sizeP}px ${family}, sans-serif`;
  }

  /* ─────────────────────────────────────────────────────────
     测量文字宽度（含额外 letterSpacing）
  ───────────────────────────────────────────────────────── */
  function _measureWidth(ctx, text, ls) {
    // ls = 0 时直接用浏览器 measureText，不加任何额外间距
    const base = ctx.measureText(text).width;
    if (!ls || ls === 0) return base;
    return base + ls * Math.max(0, text.length - 1);
  }

  /* ─────────────────────────────────────────────────────────
     measureFit：自动缩小字号直到文字宽度 ≤ maxWidth
  ───────────────────────────────────────────────────────── */
  function _measureFit(ctx, text, fontName, initSize, ls, weight, maxWidth, minSize) {
    weight  = weight  || 700;
    minSize = minSize || 10;
    ls      = isScriptFont(fontName) ? 0 : (ls || 0);

    let fs = initSize;
    ctx.font = _buildFont(weight, fs, fontName);

    while (fs > minSize && _measureWidth(ctx, text, ls) > maxWidth) {
      fs -= 0.5;
      ctx.font = _buildFont(weight, fs, fontName);
    }
    return Math.max(minSize, fs);
  }

  /* ─────────────────────────────────────────────────────────
     drawWithSpacing：逐字符绘制（仅在 ls !== 0 时使用）
     花体字走 fillText 整体绘制路径，保证连笔
  ───────────────────────────────────────────────────────── */
  function _drawText(ctx, text, x, y, ls) {
    if (!ls || ls === 0) {
      ctx.fillText(text, x, y);
      return;
    }
    let cx = x;
    for (const ch of text) {
      ctx.fillText(ch, cx, y);
      cx += ctx.measureText(ch).width + ls;
    }
  }

  /* ─────────────────────────────────────────────────────────
     计算文字 x 起点（根据 align）
  ───────────────────────────────────────────────────────── */
  function _getStartX(align, lineX, maxWidth, textWidth) {
    if (align === 'center') return lineX + (maxWidth - textWidth) / 2;
    if (align === 'right')  return lineX + maxWidth - textWidth;
    return lineX; // 'left'
  }

  /* ─────────────────────────────────────────────────────────
     主 API：drawLines
  ───────────────────────────────────────────────────────── */
  /**
   * 在 Canvas 上渲染多行文字，完全复刻产品9的花体连笔效果。
   *
   * @param  {CanvasRenderingContext2D} ctx
   * @param  {Object} options  详见文件头注释
   * @returns {{ renderedLines: Array }}
   */
  function drawLines(ctx, options) {
    const {
      lines         = [],
      x             = 0,
      maxWidth      = 200,
      canvasHeight  = 320,
      align         = 'left',
      groupGap      = 4,
      offsetY       = 0,
      placeholderText,
      placeholderFont,
      placeholderSize,
      placeholderColor = '#d0c8c0'
    } = options;

    if (!ctx || !lines.length) return { renderedLines: [] };

    // ── 过滤掉空行，但保留行的原始索引用于 groupGap 判断 ──
    const activeLines = lines
      .map((l, i) => ({ ...l, _origIndex: i }))
      .filter(l => l.text && l.text.trim());

    // ── 所有行都为空时渲染占位文字 ──
    if (!activeLines.length) {
      if (placeholderText) {
        const firstLine = lines[0] || {};
        const phFont    = placeholderFont || firstLine.font || 'Montserrat';
        const phSize    = placeholderSize || (firstLine.size || 24) * 0.82;
        const phWeight  = firstLine.weight || 700;
        ctx.save();
        ctx.fillStyle    = placeholderColor;
        ctx.font         = _buildFont(phWeight, Math.round(phSize), phFont);
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(placeholderText, x, canvasHeight / 2 + offsetY);
        ctx.restore();
      }
      return { renderedLines: [] };
    }

    // ── 第一遍：为每个有内容的行计算实际渲染字号和行高 ──
    const measured = activeLines.map(line => {
      const fontName = line.font   || 'Montserrat';
      const weight   = line.weight || 700;
      const initSize = line.size   || 20;
      const minSize  = line.minSize || 10;
      // 花体字字间距强制为 0
      const ls       = isScriptFont(fontName) ? 0 : (line.letterSpacing || 0);
      const lhFactor = line.lineHeightFactor || 1.2;

      const fs = _measureFit(ctx, line.text, fontName, initSize, ls, weight, maxWidth, minSize);
      const lineH = fs * lhFactor;

      return { ...line, _fontName: fontName, _weight: weight, _ls: ls, _fs: fs, _lineH: lineH };
    });

    // ── 第二遍：计算总高度（含 groupGap）并确定起始 Y ──
    // groupGap 施加于第0行（_origIndex===0）与第1行之间
    const hasGroup0 = measured.some(l => l._origIndex === 0);
    const hasRest   = measured.some(l => l._origIndex > 0);
    const effectiveGap = (hasGroup0 && hasRest) ? groupGap : 0;

    const totalH = measured.reduce((s, l) => s + l._lineH, 0) + effectiveGap;

    // 整体垂直居中 + 拖拽偏移
    let curY = (canvasHeight - totalH) / 2 + measured[0]._lineH / 2 + offsetY;

    // ── 第三遍：正式绘制 ──
    ctx.save();
    ctx.textBaseline = 'middle';

    measured.forEach((item, i) => {
      const color = item.color || ctx.fillStyle || '#1a1a1a';
      ctx.fillStyle = color;
      ctx.font      = _buildFont(item._weight, item._fs, item._fontName);

      // 计算该行实际宽度（用于 center/right 对齐）
      const textW   = _measureWidth(ctx, item.text, item._ls);
      const drawX   = _getStartX(align, x, maxWidth, textW);

      _drawText(ctx, item.text, drawX, curY, item._ls);

      // 推进 Y：当前行下半 + 下一行上半 + 可能的 groupGap
      if (i < measured.length - 1) {
        const isAfterGroup0 = (item._origIndex === 0) && hasRest;
        const gap = isAfterGroup0 ? effectiveGap : 0;
        curY += item._lineH / 2 + measured[i + 1]._lineH / 2 + gap;
      }
    });

    ctx.restore();

    // ── 返回实际渲染参数（供宿主调试/二次布局）──
    return {
      renderedLines: measured.map(l => ({
        text:  l.text,
        font:  l._fontName,
        size:  l._fs,
        lineH: l._lineH
      }))
    };
  }

  /* ─────────────────────────────────────────────────────────
     公开 API
  ───────────────────────────────────────────────────────── */
  return {
    drawLines,
    isScriptFont
  };

})();